/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   4/25/22
 */

import React from "react";
import {Stack, Typography} from "@mui/material";
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineDot from '@mui/lab/TimelineDot';
import GroupIcon from '@mui/icons-material/Group';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SendIcon from '@mui/icons-material/Send';
import {LoadingButton} from "@mui/lab";
import ConfigWhen from "./ConfigWhen";
import {EventSubscriptionConfig, SlackConversation} from "../../interfaces/Global";
import ConfigEmployeesFilter from "./ConfigEmployeesFilter";
import ConfigEventsFilter from "./ConfigEventsFilter";
import ConfigSettings from "./ConfigSettings";
import ErrorsDisplay from "../../components/ErrorsDisplay";
import {useMutation} from "urql";
import {
	CreateEventSubscriptionMutation,
	CreateEventSubscriptionResponse, CreateEventSubscriptionVars, EventSubscriptionInput,
	UpdateEventSubscriptionMutation, UpdateEventSubscriptionResponse, UpdateEventSubscriptionVars
} from "../../graphql/mutations/event-subscriptions";
import {useNavigate} from "react-router-dom";
import ConfigDelivery from "./ConfigDelivery";

export interface EventSubscriptionFormProps {
	id?: number
	disabled?: boolean
	config: EventSubscriptionConfig
}

export default function SubscriptionForm(props: EventSubscriptionFormProps) {

	const navigate = useNavigate();

	const [types, setTypes] = React.useState(props.config?.types || []);
	const [employeesFilter, setEmployeesFilter] = React.useState(props.config?.employeesFilter || []);
	const [eventsFilter, setEventsFilter] = React.useState(props.config?.eventsFilter || []);
	const [calendarId, setCalendarId] = React.useState(props.config?.calendarId);
	const [includeApproveButton, setIncludeApproveButton] = React.useState(props.config?.includeApproveButton ?? false);
	const [messageFormat, setMessageFormat] = React.useState(props.config?.messageFormat || "standard");
	const [channel, setChannel] = React.useState<SlackConversation|undefined>(props.config?.channel ?? undefined);
	const [deliverTo, setDeliverTo] = React.useState(channel || window.location.href.includes("?code=") ? "channel" : "self");

	const [errors, setErrors] = React.useState<Record<string, string>>({});
	const [submitEnabled, setSubmitEnabled] = React.useState(false);

	const [createResult, createEventSubscription] = useMutation<CreateEventSubscriptionResponse, CreateEventSubscriptionVars>(CreateEventSubscriptionMutation);
	const [updateResult, updateEventSubscription] = useMutation<UpdateEventSubscriptionResponse, UpdateEventSubscriptionVars>(UpdateEventSubscriptionMutation);

	const submit = () => {
		if (Object.keys(errors).length !== 0) return;
		const data: EventSubscriptionInput = { config: { types, employeesFilter, eventsFilter, calendarId, includeApproveButton, messageFormat, channel } };
		const promise = !props.id ? createEventSubscription({ data }) : updateEventSubscription({ id: props.id as number, data });
		promise.then(response => {
			if (response.data) {
				navigate("/notifications");
			}
		});
	}

	React.useEffect(() => {
		if (deliverTo === "channel" && !channel) {
			setErrors(errors => ({ ...errors, channel: 'Please specify a Slack channel to deliver notifications to.'}));
		} else {
			setErrors(errors => { const {channel: omit, ...rest} = errors; return rest; });
		}
	}, [deliverTo, channel])

	React.useEffect(() => {
		if (types.length === 0) {
			setErrors(errors => ({ ...errors, types: 'At least one "when" condition must be specified.'}));
		} else {
			setErrors(errors => { const {types: omit, ...rest} = errors; return rest; });
		}
	}, [types]);

	React.useEffect(() => {
		if (employeesFilter.length > 0) {
			const isValid = employeesFilter.every(f => f[2].length > 0);
			if (!isValid) {
				setErrors(errors => ({ ...errors, employeesFilter: 'Configuration is incomplete.'}));
			} else {
				setErrors(errors => { const {employeesFilter: omit, ...rest} = errors; return rest; });
			}
		} else {
			setErrors(errors => { const {employeesFilter: omit, ...rest} = errors; return rest; });
		}
	}, [employeesFilter]);

	React.useEffect(() => {
		if (eventsFilter.length > 0) {
			const isValid = eventsFilter.every(f => f[2].length > 0);
			if (!isValid) {
				setErrors(errors => ({ ...errors, eventsFilter: 'Configuration is incomplete.'}));
			} else {
				setErrors(errors => { const {eventsFilter: omit, ...rest} = errors; return rest; });
			}
		} else {
			setErrors(errors => { const {eventsFilter: omit, ...rest} = errors; return rest; });
		}
	}, [eventsFilter]);

	React.useEffect(() => {
		setSubmitEnabled(Object.keys(errors).length === 0);
	}, [errors]);

	return (
		<Timeline position={"left"}>
			<TimelineItem>
				<TimelineOppositeContent sx={{ flexGrow: 5 }}>
					<Stack sx={{ width: "60%" }}>
						<ConfigDelivery
							deliverTo={deliverTo}
							setDeliverTo={setDeliverTo}
							slackChannel={channel}
							setSlackChannel={(channel) => setChannel(channel ? channel : undefined)}
							slackChannelError={errors.channel}
							disabled={props.disabled}
						/>
					</Stack>
				</TimelineOppositeContent>
				<TimelineSeparator>
					<TimelineDot>
						<SendIcon />
					</TimelineDot>
					<TimelineConnector />
				</TimelineSeparator>
				<TimelineContent sx={{ py: '12px', px: 2 }}>
					<Typography variant="h6" component="span">
						Delivery
					</Typography>
				</TimelineContent>
			</TimelineItem>
			<TimelineItem>
				<TimelineOppositeContent sx={{ flexGrow: 5 }}>
					<Stack sx={{ width: "60%" }}>
						<ConfigWhen
							types={types}
							onChange={setTypes}
							disabled={props.disabled}
						/>
					</Stack>
				</TimelineOppositeContent>
				<TimelineSeparator>
					<TimelineConnector sx={{ visibility: "hidden"}} />
					<TimelineDot>
						<DateRangeIcon />
					</TimelineDot>
					<TimelineConnector sx={{ height: "100%" }} />
				</TimelineSeparator>
				<TimelineContent sx={{ py: '12px', px: 2 }}>
					<Typography variant="h6" component="span">
						When
					</Typography>
				</TimelineContent>
			</TimelineItem>
			<TimelineItem>
				<TimelineOppositeContent sx={{ flexGrow: 5 }}>
					<Stack>
						<ConfigEmployeesFilter
							filter={employeesFilter}
							setFilter={setEmployeesFilter}
							disabled={props.disabled}
						/>
					</Stack>
				</TimelineOppositeContent>
				<TimelineSeparator>
					<TimelineDot>
						<GroupIcon />
					</TimelineDot>
					<TimelineConnector />
				</TimelineSeparator>
				<TimelineContent sx={{ py: '12px', px: 2 }}>
					<Typography variant="h6" component="span">
						Employees
					</Typography>
				</TimelineContent>
			</TimelineItem>
			<TimelineItem>
				<TimelineOppositeContent sx={{ flexGrow: 5 }}>
					<Stack>
						<ConfigEventsFilter
							filter={eventsFilter}
							setFilter={setEventsFilter}
							disabled={props.disabled}
						/>
					</Stack>
				</TimelineOppositeContent>
				<TimelineSeparator>
					<TimelineDot>
						<EventIcon />
					</TimelineDot>
					<TimelineConnector />
				</TimelineSeparator>
				<TimelineContent sx={{ py: '12px', px: 2 }}>
					<Typography variant="h6" component="span">
						Event Criteria
					</Typography>
				</TimelineContent>
			</TimelineItem>
			<TimelineItem>
				<TimelineOppositeContent sx={{ flexGrow: 5 }}>
					<Stack sx={{ width: "60%" }}>
						<ConfigSettings
							calendarId={calendarId}
							setCalendarId={setCalendarId}
							includeApproveButton={includeApproveButton}
							setIncludeApproveButton={setIncludeApproveButton}
							messageFormat={messageFormat}
							setMessageFormat={setMessageFormat}
							disabled={props.disabled}
						/>
					</Stack>
				</TimelineOppositeContent>
				<TimelineSeparator>
					<TimelineDot>
						<SettingsIcon />
					</TimelineDot>
					<TimelineConnector />
				</TimelineSeparator>
				<TimelineContent sx={{ py: '12px', px: 2 }}>
					<Typography variant="h6" component="span">
						Settings
					</Typography>
				</TimelineContent>
			</TimelineItem>
			{Object.keys(errors).length > 0 &&
				<TimelineItem>
					<TimelineOppositeContent sx={{ flexGrow: 5 }}>
						<Stack sx={{ width: "60%" }}>
							<ErrorsDisplay
								errors={errors}
							/>
						</Stack>
					</TimelineOppositeContent>
					<TimelineSeparator>
						<TimelineDot>
							<WarningIcon />
						</TimelineDot>
						<TimelineConnector />
					</TimelineSeparator>
					<TimelineContent sx={{ py: '12px', px: 2 }}>
						<Typography variant="h6" component="span">
							Errors
						</Typography>
					</TimelineContent>
				</TimelineItem>}
			<TimelineItem>
				<TimelineOppositeContent sx={{ flexGrow: 5 }}>
					<LoadingButton
						variant={"contained"}
						size={"large"}
						sx={{ width: "300px" }}
						loadingPosition={"start"}
						startIcon={<CheckIcon />}
						onClick={submit}
						disabled={!submitEnabled || props.disabled}
						loading={createResult.fetching || updateResult.fetching}
					>
						{props.id ? "Update" : "Create"} Notification
					</LoadingButton>
				</TimelineOppositeContent>
				<TimelineSeparator>
					<TimelineDot>
						<CheckIcon />
					</TimelineDot>
				</TimelineSeparator>
				<TimelineContent sx={{ py: '12px', px: 2 }} />
			</TimelineItem>
		</Timeline>
	);

}